<template>
	<section class="scene-users-index">
		<ChalkyUtilityTabGroup
			v-bind:tabs="['Nearby', 'Active']"
			v-bind:useUrl="true"
			v-on:tab-change="Handle_OnTabChange"
			v-slot="{ activeTab }"
		>
			<div v-if="activeTab === 'nearby'">
				<ViewUserList
					emptyState="loading"
					v-bind:userCollection="nearbyUserCollection"
					v-on:user:select="Handle_OnSelectUser"
				/>
			</div>

			<div v-if="activeTab === 'active'">
				<ViewUserList
					emptyState="loading"
					v-bind:userCollection="activeUserCollection"
					v-on:user:select="Handle_OnSelectUser"
				/>
			</div>
		</ChalkyUtilityTabGroup>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import SceneBase from '@/Core/Scene.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { mounted } from '@/Utility/Decorators';

	/**
	 * Users index scene with tabbed navigation for nearby and active
	 * users. Handles tab state, collection fetching, and location
	 * coordinate setup.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Scene/Users
	 * @project ChalkySticks App
	 */
	@Component
	export default class SceneUsersIndex extends SceneBase {
		/**
		 * Collection of active users.
		 *
		 * @type ChalkySticks.Collection.User
		 */
		@Prop()
		public activeUserCollection!: ChalkySticks.Collection.User;

		/**
		 * Collection of nearby users.
		 *
		 * @type ChalkySticks.Collection.User
		 */
		@Prop()
		public nearbyUserCollection!: ChalkySticks.Collection.User;

		/**
		 * Current active tab slug.
		 *
		 * @type string
		 */
		protected currentActiveTab: string = '';

		/**
		 * Initialize scene, set location coordinates, and fetch data.
		 *
		 * @return void
		 */
		@mounted
		protected setup(): void {
			this.currentActiveTab = this.$route.query.tab?.toString() || 'nearby';

			this.setLocationCoordinates();

			switch (this.$route.query.tab) {
				case 'active':
					this.fetchActive();
					break;

				case 'nearby':
				default:
					this.fetchNearby();
					break;
			}
		}

		/**
		 * Fetch active users.
		 *
		 * @return void
		 */
		protected fetchActive(): void {
			if (Date.now() - this.activeUserCollection.timeCompleted < 1000 * 60) {
				return;
			}

			this.activeUserCollection.fetch();
		}

		/**
		 * Fetch nearby users.
		 *
		 * @return void
		 */
		protected fetchNearby(): void {
			if (Date.now() - this.nearbyUserCollection.timeCompleted < 1000 * 60) {
				return;
			}

			this.nearbyUserCollection.fetch();
		}

		/**
		 * Gets the currently active collection based on selected tab.
		 *
		 * @return ChalkySticks.Collection.User
		 */
		protected getActiveCollection(): ChalkySticks.Collection.User {
			return this.currentActiveTab === 'active' ? this.activeUserCollection : this.nearbyUserCollection;
		}

		/**
		 * Fetches next page of results for the active collection.
		 * Called by the parent Page via $refs when AutoScroll triggers.
		 *
		 * @return Promise<void>
		 */
		public async loadMore(): Promise<void> {
			try {
				const activeCollection = this.getActiveCollection();

				// 5 is an arbitrary amount to say that we have enough
				// results where we're probably scrolling
				if (activeCollection.length > 5) {
					await activeCollection.nextPage(true);
				}
			} catch (error) {
				console.error('Error fetching more users:', error);
			}
		}

		/**
		 * Set location coordinates on the nearby user collection.
		 *
		 * @return void
		 */
		protected setLocationCoordinates(): void {
			const { latitude, longitude } = this.$store.getters['location/roughCoordinates'];

			this.nearbyUserCollection.setQueryParams({
				latitude: latitude,
				longitude: longitude,
			});
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Handle user selection from list. Emits to parent for navigation.
		 *
		 * @param PointerEvent e
		 * @param ChalkySticks.Model.User userModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectUser(e: PointerEvent, userModel: ChalkySticks.Model.User): Promise<void> {
			this.$emit('user:select', e, userModel);
		}

		/**
		 * Handle tab change events.
		 *
		 * @param string slug
		 * @return Promise<void>
		 */
		protected async Handle_OnTabChange(slug: string): Promise<void> {
			this.currentActiveTab = slug;

			// Reset pages on both collections
			this.activeUserCollection.setQueryParam('page', '1');
			this.nearbyUserCollection.setQueryParam('page', '1');

			// Track event
			ChalkySticks.Utility.Analytics.track('users:tab.change', {
				tab: slug,
			});

			switch (slug) {
				case 'nearby':
					this.fetchNearby();
					break;

				case 'active':
					this.fetchActive();
					break;

				default:
					console.warn('Unknown tab slug:', slug);
			}
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	// Layout
	// ---------------------------------------------------------------------------

	.scene-users-index {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
	}
</style>
